import { Button } from "@admin/components/atoms/Button";
import { ErrorMessage } from "@admin/components/atoms/ErrorMessage";
import { Input } from "@admin/components/atoms/Input";
import { PasswordInput } from "@admin/components/atoms/Input/PasswordInput";
import { login, useAuthStore } from "@enos-monorepo/lib";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { Trans, useTranslation } from "react-i18next";
import { Navigate, useNavigate } from "react-router";
import { z } from "zod";

const loginSchema = z.object({
  code: z.coerce.number().int().positive(),
  userName: z.string().min(1),
  password: z.string().min(1),
});

type LoginData = z.infer<typeof loginSchema>;

export default function LoginPage() {
  const { t } = useTranslation();

  const { login: storeLogin, isAuthenticated, user } = useAuthStore();

  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      if (user?.role === "SuperAdmin") {
        navigate("/super-admin/all-companies");
      } else {
        navigate("/company/locations");
      }
    }
  }, [isAuthenticated, navigate, user?.role]);

  const {
    handleSubmit,
    register,
    formState: state,
  } = useForm<LoginData>({
    resolver: zodResolver(loginSchema),
  });

  const mutation = useMutation({
    mutationFn: login,
    onSuccess: ({ data }) => {
      if (data && data.token && data.refreshToken) {
        storeLogin(data.token, data.refreshToken);
        navigate("/company/locations");
      }
    },
    onError: () => {
      toast.error(t("auth.Invalid credentials"));
    },
  });

  if (isAuthenticated) {
    <Navigate to="/company/locations" />;
  }

  return (
    <div className="grid lg:grid-cols-2 grow bg-[#FAFAFA]">
      <div className="flex justify-center items-center p-8 lg:p-10 order-2 lg:order-1">
        <div className="card max-w-[370px] w-full">
          <form
            className="card-body flex flex-col gap-5 p-10"
            onSubmit={handleSubmit((data) => {
              mutation.mutate(data);
            })}
          >
            <div className="text-center mb-2.5">
              <h3 className="text-lg font-medium text-gray-900 leading-none mb-2.5">
                <Trans
                  i18nKey="auth.sign in to"
                  components={{ span: <span className="text-brand" /> }}
                />
              </h3>
            </div>

            <div className="flex flex-col gap-1">
              <label className="form-label font-normal text-gray-900">
                {t("auth.Code")}
              </label>
              <Input
                placeholder="1234"
                maxLength={4}
                onInput={(e) => {
                  e.currentTarget.value = e.currentTarget.value.replace(
                    /\D/g,
                    "",
                  );
                }}
                {...register("code")}
              />
              <ErrorMessage message={state.errors?.code?.message} />
            </div>
            <div className="flex flex-col gap-1">
              <label className="form-label font-normal text-gray-900">
                {t("common.Username")}
              </label>
              <Input
                placeholder={t("auth.Enter Username")}
                {...register("userName")}
              />
              <ErrorMessage message={state.errors?.userName?.message} />
            </div>
            <div className="flex flex-col gap-1">
              <div className="flex items-center justify-between gap-1">
                <label className="form-label font-normal text-gray-900">
                  {t("common.Password")}
                </label>
                {/* <a
                  className="text-2sm link shrink-0"
                  href="html/demo6/authentication/branded/reset-password/enter-email.html"
                >
                  {t("auth.Forgot Password")}?
                </a> */}
              </div>
              <PasswordInput
                placeholder={t("common.Enter Password")}
                {...register("password")}
              />
              <ErrorMessage message={state.errors?.password?.message} />
            </div>
            {/* <label className="checkbox-group">
              <input className="checkbox checkbox-sm" type="checkbox" />
              <span className="checkbox-label">{t("auth.Remember me")}</span>
            </label> */}

            <Button type="submit" block loading={mutation.isPending}>
              {t("auth.Sign In")}
            </Button>
          </form>
        </div>
      </div>
      <div
        className="lg:rounded-xl lg:border lg:border-gray-200 lg:m-5 order-1 lg:order-2 bg-top xxl:bg-center xl:bg-cover bg-no-repeat"
        style={{ backgroundImage: "url(/media/backgrounds/auth-bg.png)" }}
      >
        <div className="flex flex-col p-8 lg:p-16 gap-4">
          <img
            className="max-w-none"
            src="/media/logos/enos-logo.png"
            alt="Enos Logo"
            width={151}
            height={56}
          />
          <div className="flex flex-col gap-3">
            <h3 className="text-2xl font-semibold text-brand">
              enos management v3
            </h3>
            <div className="text-base font-medium text-gray-600">
              {t("auth.Your new way to access enos applications")} <br />
              {t("auth.Please sign in to use your modules.")}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
